import { useEffect, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import api from 'services/api'
import Intro from './Intro'
import ConfigEditor from './ConfigEditor'
import Divider from '@mui/material/Divider'
import Test from './Test'
import Publish from './Publish'
import AutoTrimTest from './AutoTrimTest'
import AutoTrimCompare from './AutoTrimCompare'
import AutoTrimBatch from './AutoTrimBatch'
import AnimationCompare from './AnimationCompare'

const TAB_AUTO_TRIM = 'auto_trim'
const TAB_ANIMATION = 'animation'
const TAB_OPENAI = 'openai'

const SUB_TEST = 'test'
const SUB_COMPARE = 'compare'
const SUB_BATCH = 'batch'

const GPT = () => {
  const [tab, setTab] = useState(TAB_ANIMATION)
  const [subTab, setSubTab] = useState(SUB_TEST)
  const [configs, setConfigs] = useState(null)
  const [models, setModels] = useState(null)
  const [config, setConfig] = useState(null)
  const [testResponse, setTestResponse] = useState(null)
  const [configChanged, setConfigChanged] = useState(false)
  const [isSavingConfig, setIsSavingConfig] = useState(false)
  const [isTestingConfig, setIsTestingConfig] = useState(false)
  const initialConfig = useRef(null)
  const openaiLoaded = useRef(false)

  const resetConfig = useCallback((config) => {
    initialConfig.current = { ...config }
    setConfig(config)
    setConfigChanged(false)
  }, [])

  const updateConfig = useCallback((field, value) => {
    setConfig((config) => ({
      ...config,
      [field]: value,
    }))
    setConfigChanged(true)
  }, [])

  const saveConfig = useCallback(() => {
    setIsSavingConfig(true)
    setTestResponse(null)
    api.openai.saveConfig(config).then((savedConfig) => {
      resetConfig(savedConfig)
      setIsSavingConfig(false)
    })
  }, [config, resetConfig])

  const cancelConfigChanges = useCallback(() => {
    resetConfig(initialConfig.current)
  }, [resetConfig])

  const testConfig = useCallback(
    (text) => {
      setIsTestingConfig(true)
      setTestResponse(null)
      api.openai.testConfig(text, config).then((response) => {
        setTestResponse(response)
        setIsTestingConfig(false)
      })
    },
    [config]
  )

  const clearTestResponse = useCallback(() => {
    setTestResponse(null)
  }, [])

  useEffect(() => {
    if (tab !== TAB_OPENAI || openaiLoaded.current) return
    openaiLoaded.current = true
    api.openai.getMetadata().then((meta) => {
      setConfigs(meta.configs)
      setModels(meta.models)
      const defaultConfig = meta.configs.find((c) => c.type === 'autotitles')
      resetConfig(defaultConfig || meta.configs[0])
    })
  }, [tab, resetConfig])

  const handleTabChange = useCallback((_, newTab) => {
    if (newTab) setTab(newTab)
  }, [])

  const handleSubTabChange = useCallback((_, newSub) => {
    if (newSub !== null) setSubTab(newSub)
  }, [])

  return (
    <Box
      sx={{
        p: 4,
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={tab}
          exclusive
          onChange={handleTabChange}
          size="small"
        >
          <ToggleButton value={TAB_ANIMATION}>Animation</ToggleButton>
          <ToggleButton value={TAB_AUTO_TRIM}>Auto Trim</ToggleButton>
          <ToggleButton value={TAB_OPENAI}>OpenAI</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {tab === TAB_AUTO_TRIM && (
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Tabs
              value={subTab}
              onChange={handleSubTabChange}
              sx={{
                minHeight: 32,
                '& .MuiTab-root': {
                  minHeight: 32,
                  py: 0.5,
                  px: 2,
                  fontSize: '0.82em',
                  textTransform: 'none',
                },
              }}
            >
              <Tab label="Test" value={SUB_TEST} />
              <Tab label="Compare" value={SUB_COMPARE} />
              <Tab label="Batch" value={SUB_BATCH} />
            </Tabs>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {subTab === SUB_TEST && <AutoTrimTest />}
            {subTab === SUB_COMPARE && <AutoTrimCompare />}
            {subTab === SUB_BATCH && <AutoTrimBatch />}
          </Box>
        </Box>
      )}

      {tab === TAB_ANIMATION && (
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <AnimationCompare />
        </Box>
      )}

      {tab === TAB_OPENAI && config && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ width: 800, height: '100%', overflow: 'auto' }}>
            <Intro config={config} configs={configs} setConfig={resetConfig} />
            <ConfigEditor
              config={config}
              updateConfig={updateConfig}
              models={models}
            />
          </Box>
          <Divider orientation="vertical" sx={{ mx: 4 }} />
          <Box sx={{ width: 800, height: '100%', overflow: 'auto' }}>
            <Test
              testConfig={testConfig}
              testResponse={testResponse}
              isTestingConfig={isTestingConfig}
              clearTestResponse={clearTestResponse}
            />
            <Box sx={{ display: 'none' }}>
              <Publish
                saveConfig={saveConfig}
                cancelConfigChanges={cancelConfigChanges}
                configChanged={configChanged}
                isSavingConfig={isSavingConfig}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default GPT
